import { createClient } from '@connectrpc/connect';
import { PcfExportService, PropStorageType, PropType } from './gen/pcf_export_service_pb';
import type { GroupBy, PipingSystem } from '../../types/pcfExport.types';
import { getGrpcTransport } from './transport';
import { useIntegrationDiagnosticsStore } from '../../stores/useIntegrationDiagnosticsStore';
import { pollRevitOperation, type RevitOperationProgressSnapshot } from './operationProgressClient';
import { normalizeCollectedPipingSystems, type GrpcPipingSystem } from './pcfExportSystemNormalization';
import {
  buildE2eMockPipingSystems,
  getE2eMockSystemsMode,
  isE2eMockSystemsRequested,
} from './pcfExportE2eSystems';
import { webLog } from './webLogger';

export { runExportFlow, runPickExportFlow } from './pcfExportFlow';
export { selectExportFilesForSystems } from './pcfExportSelection';
export { PCF_EXPORT_QUOTA_EXHAUSTED_CODE } from './pcfRunApi';
export { isE2eMockSystemsRequested } from './pcfExportE2eSystems';
export type {
  ExportFlowStep,
  RunExportFlowOptions,
  RunExportFlowResult,
  RunPickExportFlowResult,
} from './pcfExportFlow';

type CollectResult = {
  success: boolean;
  systems: PipingSystem[];
  error?: string;
};

export type PcfConfigurationProperty = {
  name: string;
  dataType: string;
  category: string;
  isReadOnly: boolean;
};

type PcfConfigurationResult = {
  success: boolean;
  properties: PcfConfigurationProperty[];
  error?: string;
};

function getPcfExportClient() {
  const transport = getGrpcTransport();
  if (!transport) {
    return null;
  }

  return createClient(PcfExportService, transport);
}

function mapPropStorageTypeToDataType(storageType: PropStorageType): string {
  switch (storageType) {
    case PropStorageType.INTEGER:
      return 'integer';
    case PropStorageType.ELEMENT_ID:
      return 'element-id';
    case PropStorageType.DOUBLE:
      return 'double';
    case PropStorageType.BOOLEAN:
      return 'boolean';
    case PropStorageType.STRING:
    default:
      return 'string';
  }
}

function mapPropTypeToCategory(propType: PropType, isShared: boolean): string {
  const base = propType === PropType.TYPE ? 'Type' : 'Instance';
  return isShared ? `${base} (Shared)` : base;
}

function normalizeSystems(systems: readonly GrpcPipingSystem[]): PipingSystem[] {
  const normalized = normalizeCollectedPipingSystems(systems);
  if (normalized.skippedEmptySystemNames.length > 0) {
    webLog.warn(
      'export-orchestrator',
      `[collect-systems-empty-skipped] count=${normalized.skippedEmptySystemNames.length}`,
    );
  }
  return [...normalized.systems];
}

export async function collectPipingSystemsGrpc(
  groupBy: GroupBy,
  options?: {
    includeLinkedModels?: boolean;
    onProgress?: (progress: RevitOperationProgressSnapshot) => void;
  },
): Promise<CollectResult> {
  const e2eMockSystemsMode = getE2eMockSystemsMode();
  if (e2eMockSystemsMode !== null) {
    return { success: true, systems: buildE2eMockPipingSystems(e2eMockSystemsMode) };
  }

  const client = getPcfExportClient();
  if (!client) {
    useIntegrationDiagnosticsStore.getState().setLastGrpcError('CollectSystems: gRPC transport is unavailable');
    return {
      success: false,
      systems: [],
      error: 'gRPC transport is unavailable',
    };
  }

  try {
    // Collection always enumerates piping systems; parameter grouping is
    // resolved later during calculation. Only system-type grouping changes
    // which name the plugin collects.
    const request = {
      bySystemType: groupBy === 'SystemTypeName',
      includeLinkedModels: options?.includeLinkedModels ?? false,
    };
    if (options?.onProgress) {
      try {
        const startResponse = await client.startCollectSystems(request);
        if (startResponse.isSuccess && startResponse.operationId) {
          const operationResult = await pollRevitOperation(startResponse.operationId, {
            onProgress: options.onProgress,
            parseResult: parseCollectSystemsResultJson,
          });
          if (!operationResult.success || !operationResult.result) {
            const error = operationResult.error || 'CollectSystems operation failed';
            useIntegrationDiagnosticsStore.getState().setLastGrpcError(`CollectSystems: ${error}`);
            return { success: false, systems: [], error };
          }
          return operationResult.result;
        }
      } catch {
        // Older connector builds only expose the unary method.
      }
    }

    const response = await client.collectSystems(request);
    if (!response.isSuccess) {
      useIntegrationDiagnosticsStore.getState().setLastGrpcError(`CollectSystems: ${response.error || 'failed'}`);
      return {
        success: false,
        systems: [],
        error: response.error || 'CollectSystems failed',
      };
    }

    const systems: GrpcPipingSystem[] = response.systems.map((x) => ({
      Name: x.name,
      RevitId: Number(x.revitId),
      Props: { ...x.props },
    }));

    useIntegrationDiagnosticsStore.getState().setLastGrpcError(null);

    return {
      success: true,
      systems: normalizeSystems(systems),
    };
  } catch (error) {
    const message = error instanceof Error ? error.message : 'CollectSystems gRPC error';
    useIntegrationDiagnosticsStore.getState().setLastGrpcError(`CollectSystems: ${message}`);
    return {
      success: false,
      systems: [],
      error: message,
    };
  }
}

export async function getPcfConfigurationPropertiesGrpc(): Promise<PcfConfigurationResult> {
  const client = getPcfExportClient();
  if (!client) {
    useIntegrationDiagnosticsStore.getState().setLastGrpcError('GetPcfConfiguration: gRPC transport is unavailable');
    return {
      success: false,
      properties: [],
      error: 'gRPC transport is unavailable',
    };
  }

  try {
    const response = await client.getPcfConfiguration({});
    if (!response.isSuccess) {
      const errorMessage = response.error || 'GetPcfConfiguration failed';
      useIntegrationDiagnosticsStore.getState().setLastGrpcError(`GetPcfConfiguration: ${errorMessage}`);
      return {
        success: false,
        properties: [],
        error: errorMessage,
      };
    }

    const propertiesMap = new Map<string, PcfConfigurationProperty>();
    response.elementProps.forEach((prop) => {
      const name = String(prop.name || '').trim();
      if (!name || propertiesMap.has(name)) {
        return;
      }

      propertiesMap.set(name, {
        name,
        dataType: mapPropStorageTypeToDataType(prop.propertyType),
        category: mapPropTypeToCategory(prop.propType, prop.isShared),
        isReadOnly: false,
      });
    });

    useIntegrationDiagnosticsStore.getState().setLastGrpcError(null);

    return {
      success: true,
      properties: Array.from(propertiesMap.values()).sort((a, b) => a.name.localeCompare(b.name)),
    };
  } catch (error) {
    const message = error instanceof Error ? error.message : 'GetPcfConfiguration gRPC error';
    useIntegrationDiagnosticsStore.getState().setLastGrpcError(`GetPcfConfiguration: ${message}`);
    return {
      success: false,
      properties: [],
      error: message,
    };
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function parseCollectSystemsResultJson(resultJson: string): CollectResult {
  const parsed: unknown = JSON.parse(resultJson);
  if (!isRecord(parsed) || parsed.isSuccess !== true) {
    return {
      success: false,
      systems: [],
      error: isRecord(parsed) && typeof parsed.error === 'string'
        ? parsed.error
        : 'CollectSystems failed',
    };
  }

  const systems: GrpcPipingSystem[] = Array.isArray(parsed.systems)
    ? parsed.systems.filter(isRecord).map((system) => {
        const props = isRecord(system.props)
          ? Object.fromEntries(
            Object.entries(system.props)
              .filter((entry): entry is [string, string] => typeof entry[1] === 'string'),
          )
          : {};
        return {
          Name: typeof system.name === 'string' ? system.name : '',
          RevitId: Number(system.revitId),
          Props: props,
        };
      })
    : [];

  return {
    success: true,
    systems: normalizeSystems(systems),
  };
}
